'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { Users, ChevronRight, RefreshCw } from 'lucide-react';
import {
  getLiveWorldCupFixtures,
  getUpcomingFixtures,
  getFixtureLineups,
  isInPlay,
  getFlag,
  type LiveFixture,
  type ApiLineup,
} from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

interface LineupState {
  fixture: LiveFixture;
  lineups: ApiLineup[];
  isLive: boolean;
}

const POSITION_ORDER: Record<string, number> = {
  G: 0, GK: 0,
  D: 1, CB: 1, RB: 1, LB: 1, RWB: 1, LWB: 1,
  M: 2, CM: 2, CDM: 2, CAM: 2, RM: 2, LM: 2,
  F: 3, ST: 3, CF: 3, LW: 3, RW: 3, SS: 3,
};

function posOrder(pos: string): number {
  return POSITION_ORDER[pos?.toUpperCase()] ?? 2;
}

function PlayerItem({ number, name, pos, align }: { number: number; name: string; pos: string; align: 'left' | 'right' }) {
  return (
    <div className={`flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/5 transition-colors ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      <span
        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold font-mono flex-shrink-0"
        style={{ background: 'hsl(222 25% 18%)', color: 'hsl(222 15% 65%)' }}
      >
        {number}
      </span>
      <div className={`flex-1 min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
        <p className="text-xs font-medium text-white truncate leading-tight">{name}</p>
        <p className="text-xs text-slate-500 leading-tight">{pos}</p>
      </div>
    </div>
  );
}

export default function StartingLineupsWidget() {
  const [state, setState] = useState<LineupState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { t } = useLanguage();

  const fetchLineups = useCallback(async () => {
    try {
      setError(false);

      // 1. Try live fixtures first
      const liveFixtures = await getLiveWorldCupFixtures();
      const liveInPlay = (liveFixtures ?? []).filter(m => isInPlay(m.fixture.status));

      let targetFixture: LiveFixture | null = liveInPlay[0] ?? liveFixtures?.[0] ?? null;
      let isLive = !!targetFixture;

      // 2. Fall back to next upcoming fixture
      if (!targetFixture) {
        const upcoming = await getUpcomingFixtures(1);
        targetFixture = upcoming?.[0] ?? null;
        isLive = false;
      }

      if (!targetFixture) {
        setState(null);
        return;
      }

      // 3. Fetch lineups for the target fixture
      const lineups = await getFixtureLineups(targetFixture.fixture.id);

      setState({ fixture: targetFixture, lineups: lineups ?? [], isLive });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLineups();
    // Refresh every 60s (lineups rarely change mid-game)
    intervalRef.current = setInterval(fetchLineups, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchLineups]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <span className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</span>
        </div>
        <div className="p-4 space-y-2 animate-pulse">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-2">
              <div className="flex-1 h-8 rounded-lg" style={{ background: 'hsl(222 25% 16%)' }} />
              <div className="flex-1 h-8 rounded-lg" style={{ background: 'hsl(222 25% 16%)' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <span className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</span>
        </div>
        <div className="p-4 text-center">
          <p className="text-sm text-slate-500 mb-3">{t.dashboard.lineupsError}</p>
          <button
            onClick={() => { setLoading(true); fetchLineups(); }}
            className="flex items-center gap-1.5 mx-auto text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <RefreshCw size={12} />
            {t.common.retry}
          </button>
        </div>
      </div>
    );
  }

  // ── No fixture / no lineups yet ───────────────────────────────────────────
  if (!state) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <span className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</span>
        </div>
        <div className="p-6 text-center">
          <Users size={28} className="mx-auto text-slate-600 mb-2" />
          <p className="text-sm text-slate-500">{t.dashboard.lineupsNoFixture}</p>
        </div>
      </div>
    );
  }

  const { fixture, lineups, isLive } = state;
  const homeTeam = fixture.teams.home;
  const awayTeam = fixture.teams.away;

  const homeLineup = lineups.find(l => l.team.id === homeTeam.id);
  const awayLineup = lineups.find(l => l.team.id === awayTeam.id);

  // Sort players by position (GK → DEF → MID → FWD)
  const homePlayers = (homeLineup?.startXI ?? [])
    .map(p => p.player)
    .sort((a, b) => posOrder(a.pos) - posOrder(b.pos));
  const awayPlayers = (awayLineup?.startXI ?? [])
    .map(p => p.player)
    .sort((a, b) => posOrder(a.pos) - posOrder(b.pos));

  const hasLineups = homePlayers.length > 0 || awayPlayers.length > 0;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
        <div className="flex items-center gap-2">
          <Users size={15} className="text-slate-400" />
          <span className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</span>
          {isLive && (
            <span
              className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'hsl(0 84% 60% / 0.15)', color: 'hsl(0 84% 70%)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
              LIVE
            </span>
          )}
          {!isLive && (
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: 'hsl(189 94% 51% / 0.08)', color: 'hsl(189 94% 65%)' }}
            >
              {t.dashboard.lineupsNextGame}
            </span>
          )}
        </div>
        <Link
          href={`/game-detail?id=${fixture.fixture.id}`}
          className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          {t.dashboard.viewDetails.replace(' →', '')}
          <ChevronRight size={12} />
        </Link>
      </div>

      {/* Teams header row */}
      <div className="px-3 pt-3 pb-2 grid grid-cols-2 gap-2">
        {/* Home team */}
        <div className="flex items-center gap-2">
          {homeTeam.logo ? (
            <img src={homeTeam.logo} alt={homeTeam.name} className="w-6 h-6 object-contain flex-shrink-0" />
          ) : (
            <span className="text-lg flex-shrink-0">{getFlag(homeTeam.name)}</span>
          )}
          <div className="min-w-0">
            <p className="text-xs font-bold text-white truncate">{homeTeam.name}</p>
            {homeLineup?.formation && (
              <p className="text-xs text-slate-500">{homeLineup.formation}</p>
            )}
          </div>
        </div>
        {/* Away team */}
        <div className="flex items-center gap-2 flex-row-reverse">
          {awayTeam.logo ? (
            <img src={awayTeam.logo} alt={awayTeam.name} className="w-6 h-6 object-contain flex-shrink-0" />
          ) : (
            <span className="text-lg flex-shrink-0">{getFlag(awayTeam.name)}</span>
          )}
          <div className="min-w-0 text-right">
            <p className="text-xs font-bold text-white truncate">{awayTeam.name}</p>
            {awayLineup?.formation && (
              <p className="text-xs text-slate-500">{awayLineup.formation}</p>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-3 mb-1" style={{ height: 1, background: 'hsl(222 25% 16%)' }} />

      {/* Player lists */}
      {hasLineups ? (
        <div className="px-2 pb-3 grid grid-cols-2 gap-x-1">
          {/* Home players */}
          <div>
            {homePlayers.map(p => (
              <PlayerItem key={p.id} number={p.number} name={p.name} pos={p.pos} align="left" />
            ))}
          </div>
          {/* Away players */}
          <div>
            {awayPlayers.map(p => (
              <PlayerItem key={p.id} number={p.number} name={p.name} pos={p.pos} align="right" />
            ))}
          </div>
        </div>
      ) : (
        <div className="px-4 py-5 text-center">
          <p className="text-sm text-slate-500">{t.dashboard.lineupsNotYetReleased}</p>
          <p className="text-xs text-slate-600 mt-1">{t.dashboard.lineupsCheckBack}</p>
        </div>
      )}

      {/* Round label */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{ borderTop: '1px solid hsl(222 25% 14%)', background: 'hsl(222 35% 9%)' }}
      >
        <span className="text-xs text-slate-600">{fixture.league.round}</span>
        <span className="text-xs text-slate-600">{fixture.fixture.venue.name ?? ''}</span>
      </div>
    </div>
  );
}
